import {
    ClicksSearchList,
    DKI,
    DomainForm,
    DomainInfo,
    getDomainForm,
    getLookupsAndVariations,
    Route,
    Session,
    getClicksSearchList,
    getLocationByZipCode,
    getAliasUrl,
} from "src/api";

import {
    ApiCallType,
    AutoWarrantyMakers,
    DomainRoute,
    IComponentApiCall,
    Options,
    PageDataCalls,
    PageProps,
    TypedApiCall,
} from "@services/initialCallsTypes";
import {
    replacePlaceholders,
    getOffersByPlacement,
    orderListByKeyword,
} from "./sharedUtils";
import { ServerResponse } from "http";
import {
    Category,
    JSONValue,
    ListingItem,
    OfferListToShow,
    ServerRequest,
    TranslationContent,
    ClickOffer,
    TextBlock,
} from "@lib/shared/types";
import { setOfferId, getOfferId } from "@lib/shared/cookies";
import { sha256 } from "js-sha256";
import { getListingBySlugV3, ListingV3 } from "src/api/listings";
import { BlogPostType } from "src/api/getBlog";
import { LookupOrVariationItem } from "src/api/lookupsAndVariations";
import { PiiCompletion } from "src/api/piiCompletion";
import { ParsedUrlQuery } from "querystring";
import { RouteConfig } from "@page-builder/ts/types";
import { get } from "@lib/shared/lodash";
import { resolveOperatingSystem } from "./antiviruschoice/utils";
import { getNearbyZipCodes } from "src/api/nearbyZipCodes";
import { getTextBlocks, GetTextBlocksResponse } from "src/api/getTextBlocks";
import { getMenus } from "src/api/menus";
import { getDomainPosts } from "src/api/fetchDomainPosts";
import { GetDomainPostsParams } from "src/api/fetchDomainPosts";
import {
    DEFAULT_PER_PAGE,
    getFeaturedPosts,
} from "@page-builder/components/postsGrid";
import { TPostsGrid } from "@page-builder/ts/generated";
// import { RouteConfig } from "@page-builder/ts/types";

const prepositions = [
    "to",
    "the",
    "and",
    "at",
    "a",
    "for",
    "an",
    "but",
    "nor",
    "or",
    "so",
    "yet",
    "by",
    "in",
    "of",
    "on",
    "to",
    "up",
    "off",
    "over",
];

type PostsGridApiCall = {
    key: string;
    value: GetDomainPostsParams & {
        [key: string]: string | number | boolean | string[];
    };
};

function buildPostsGridApiCall({
    component,
    query,
}: {
    component: TPostsGrid;
    query?: ParsedUrlQuery;
}): PostsGridApiCall {
    const props = component.props ?? {};
    const postType = props?.postType || "blog-type_post";

    const postsPerPage = Number(props?.postsPerPage || DEFAULT_PER_PAGE);
    const limit = props?.showReadMorePosts ? postsPerPage + 1 : postsPerPage;

    const urlSyncEnabled = props?.urlSyncEnabled;
    const parsedPage = parseInt(query?.page as string, 10);
    const page =
        urlSyncEnabled && Number.isFinite(parsedPage) && parsedPage > 1
            ? parsedPage - 1
            : 0;

    const taxonomySlug =
        urlSyncEnabled && query?.filters
            ? (query.filters as string)?.split(",").map((slug) => slug.trim())
            : [];

    const featuredPostsIds = getFeaturedPosts(props?.featuredPosts);
    const applyFeaturedFilter = props?.applyFeaturedFilter ?? false;

    const postsParams = {
        type: postType,
        limit: String(limit),
        page,
        ...(taxonomySlug.length > 0 ? { taxonomySlug } : {}),
        ...(featuredPostsIds ? { "filter[id]": featuredPostsIds } : {}),
        ...(applyFeaturedFilter ? { "filter[featured]": true } : {}),
    };

    const requestKey = [
        postType,
        limit,
        page,
        taxonomySlug.join(","),
        featuredPostsIds,
        applyFeaturedFilter ? "featured" : "all",
    ].join(":");

    return {
        key: `getDomainPosts:${requestKey}`,
        value: postsParams,
    };
}

export const updateDomainOffersByPossibleCategory = ({
    defaultOffers,
    categorySlug,
    defaultFallbackCategory,
}: {
    defaultOffers: DomainInfo["defaultOffers"];
    categorySlug: string;
    defaultFallbackCategory: boolean;
}) => {
    if (!categorySlug || defaultFallbackCategory) {
        return defaultOffers;
    }
    return defaultOffers.filter(
        (offer) => offer.category.slug === categorySlug,
    );
};

/**
 * Maps Category object to include only essential fields needed on the client
 * This reduces the payload size by stripping unnecessary data from images and config
 */
export const mappingCategory = (
    category: Category | null | undefined,
): Category | null => {
    // console.log("translations===>", (category as Category).translations);
    // console.log("modelType===>", (category as Category).modelType);
    // console.log("hasCrossSelling===>", (category as Category).hasCrossSelling);
    if (!category) return null;

    return {
        id: category.id,
        name: category.name,
        slug: category.slug,
        label: category.label ?? null,
        slugAlias: category.slugAlias,
        title: category.title ?? null,
        crossSellingDomainFormId: category.crossSellingDomainFormId ?? null,
        isOneToOneConsent: category.isOneToOneConsent,
        icon: category.icon
            ? {
                  id: category.icon.id,
                  fullPath: category.icon.fullPath,
              }
            : null,
        image: category.image
            ? {
                  id: category.image.id,
                  fullPath: category.image.fullPath,
              }
            : null,
        config: category.config ?? null,
        description: category.description ?? null,
        headline: category.headline ?? null,
        metatags: category.metatags ?? null,
        secondServiceCategories: category.secondServiceCategories ?? null,
        parentCategory: category.parentCategory ?? null,
    } as Category;
};

/**
 * Maps DomainInfo object to include only essential fields needed on the client
 * This reduces the payload size significantly by excluding unnecessary data
 */
export const mappingDomainInfo = (
    domain: DomainInfo | null,
    props?: Partial<PageProps>,
    session?: Session | null,
): DomainInfo | null => {
    // console.log("otpVerificationCriteria===>", domain?.otpVerificationCriteria);
    // console.log("domain.templateConfig===>", domain?.templateConfig);
    // console.log("domain.menusList===>", domain?.menusList);
    // console.log("domain.modelType===>", domain?.modelType);
    // console.log("domain.leadCertificationProvider===>", domain?.leadCertificationProvider);
    // console.log("domain.templateConfig===>", domain?.domain.templateConfig);
    // console.log("domain.defaultOffers[0].domainOfferCategoryId===>", domain?.defaultOffers[0]?.domainOfferCategoryId);
    // console.log("domain.defaultOffers[0].checkWallet===>", domain?.defaultOffers[0]?.checkWallet);
    // console.log("domain.defaultOffers[0].translations===>", domain?.defaultOffers[0]?.translations);
    // console.log("domain.defaultOffers[0].workingHours===>", domain?.defaultOffers[0]?.workingHours);
    // console.log("domain.defaultOffers[0].modelType===>", domain?.defaultOffers[0]?.modelType);

    if (!domain) return null;

    const filteredDefaultOffers = updateDomainOffersByPossibleCategory({
        defaultOffers: domain.defaultOffers,
        categorySlug: props?.category?.slug ?? "",
        defaultFallbackCategory: !!props?.defaultFallbackCategory,
    });

    return {
        id: domain.id,
        name: domain.name,
        niceName: domain.niceName,
        title: domain.title,
        slogan: domain.slogan,
        type: domain.type,
        description: domain.description,
        gtmId: domain.gtmId,
        clickyCodeId: domain.clickyCodeId,
        analyticsCodeId: domain.analyticsCodeId,
        isInspectorEnabled: domain.isInspectorEnabled,
        enableJornayaId: domain.enableJornayaId,
        enableTrustedForm: domain.enableTrustedForm,
        twitterPixel: domain.twitterPixel,
        showForm: domain.showForm,
        // Only include essential image fields (id, fullPath, mimeType)
        favicon: domain.favicon
            ? {
                  fullPath: domain.favicon.fullPath,
              }
            : null,
        logo: domain.logo
            ? {
                  fullPath: domain.logo.fullPath,
              }
            : null,
        secondaryLogo: domain.secondaryLogo
            ? {
                  fullPath: domain.secondaryLogo.fullPath,
              }
            : null,
        // Keep only essential category fields
        categories: domain.categories.map((cat) => ({
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            label: cat.label,
            slugAlias: cat.slugAlias,
            title: cat.title,
            crossSellingDomainFormId: cat.crossSellingDomainFormId,
            isOneToOneConsent: cat.isOneToOneConsent,
            icon: cat.icon
                ? {
                      id: cat.icon.id ?? null,
                      fullPath: cat.icon.fullPath,
                  }
                : null,
            image: cat.image
                ? {
                      id: cat.image.id ?? null,
                      fullPath: cat.image.fullPath,
                  }
                : null,
            config: cat.config,
            secondServiceCategories: cat?.secondServiceCategories || [],
            taxonomyFieldGroups:
                cat?.taxonomyFieldGroups?.map((group) => ({
                    id: group?.id,
                    name: group?.name,
                    slug: group?.slug,
                    customFields:
                        group?.customFields?.map((field) => ({
                            fieldCodeName: field?.fieldCodeName,
                            fieldValue: field?.fieldValue,
                            taxonomyId: field?.taxonomyId,
                        })) || [],
                })) || [],
        })),
        // Keep minimal listing data
        listings: domain.listings?.map((listing) => ({
            id: listing.id,
            name: listing.name,
            slug: listing.slug,
            isComparison: listing.isComparison,
            topOffer: listing.topOffer ?? null,
            icon: listing.icon
                ? {
                      fullPath: listing.icon.fullPath,
                  }
                : null,
        })) as DomainInfo["listings"],
        // Keep essential offer fields
        defaultOffers: filteredDefaultOffers?.map((offer) => ({
            id: offer.id,
            slug: offer.slug,
            title: offer.title,
            description: offer.description,
            displayUrl: offer.displayUrl,
            features: offer.features ?? [],
            attributes: offer.attributes ?? {},
            hiddenAttributes: offer.hiddenAttributes ?? {},
            oldPrice: offer.oldPrice ?? null,
            price: offer.price ?? null,
            discount: offer.discount ?? null,
            currency: offer.currency ?? null,
            isPhoneNumber: offer.isPhoneNumber ?? null,
            phoneLabel: offer.phoneLabel ?? null,
            placements: offer.placements ?? null,
            sticky: offer.sticky ?? false,
            score: offer.score ?? null,
            previewDescription: offer.previewDescription ?? null,
            buyer: offer.buyer ?? null,
            proxiedNumber: offer.proxiedNumber
                ? {
                      phoneNumber: offer.proxiedNumber.phoneNumber,
                      offerId: offer.proxiedNumber.offerId,
                  }
                : null,
            logo: offer.logo
                ? {
                      fullPath: offer.logo.fullPath,
                  }
                : null,
            image: offer.image
                ? {
                      fullPath: offer.image.fullPath,
                  }
                : null,
            category: {
                id: offer.category.id,
                name: offer.category.name,
                slug: offer.category.slug,
            },
            taxonomyFieldGroups:
                offer?.taxonomyFieldGroups?.map((group) => ({
                    id: group?.id,
                    name: group?.name,
                    slug: group?.slug,
                    customFields:
                        group?.customFields?.map((field) => ({
                            fieldCodeName: field?.fieldCodeName,
                            fieldValue: field?.fieldValue,
                            taxonomyId: field?.taxonomyId,
                        })) || [],
                })) || [],
        })),
        proxiedNumbers: domain.proxiedNumbers ?? null,
        recaptchaConfig: domain.recaptchaConfig ?? null,
        advertisingNetworkTrackingSettings:
            domain.advertisingNetworkTrackingSettings ?? null,
        config: domain.config,
        settings: domain.settings,
        // Textblocks - keep essential fields only
        textBlocks: domain.textBlocks
            ? domain.textBlocks?.map((tb) => ({
                  id: tb.id,
                  position: tb.position,
                  text: tb.text,
                  categoryId: tb.categoryId,
                  domainId: tb.domainId,
                  isActive: tb.isActive,
                  offersId: tb.offersId ?? null,
                  queryString: (tb.queryString as string | null) ?? null,
                  translations: (tb.translations ?? null) as Record<
                      string,
                      unknown
                  > | null,
                  category: tb.category,
              }))
            : [],
        metatags: domain.metatags,
        notificationsSettings: domain.notificationsSettings,
        fallbackSettings: domain.fallbackSettings,
        translations: domain.translations,
        ...(session?.domainMenus
            ? {
                  menusList: session?.domainMenus?.map((menu) => ({
                      name: menu.name,
                      slug: menu.slug,
                      items: menu.links || [],
                  })),
              }
            : {}),
        allowedLanguages: domain.allowedLanguages,
    } as DomainInfo;
};

/**
 * Maps DomainForm object to include only essential fields needed on the client
 * This reduces the payload size by stripping unnecessary data from listings and text blocks
 */
export const mappingFormInfo = (form: DomainForm | null): DomainForm | null => {
    // console.log("form.description===>", form.description);
    // console.log("form.isVariant===>", form.isVariant);
    // console.log("form.variantParentId===>", form.variantParentId);
    // console.log("form.translations===>", form.translations);
    // console.log("form.categoryLabel===>", form.categoryLabel);
    // console.log("form.integrations===>", form.integrations);
    // console.log("form.modelType===>", form.modelType);

    if (!form) return null;
    return {
        title: form.title,
        steps: form.steps,
        id: form.id,
        stepToSendingData: form.stepToSendingData,
        successMessage: form.successMessage,
        errorMessage: form.errorMessage,
        categorySlug: form.categorySlug,
        domainFormId: form.domainFormId,
        listingId: form.listingId,
        listing: {
            id: form?.listing?.id ?? null,
            slug: form?.listing?.slug ?? null,
        },
        textBlocks: form.textBlocks
            ? form.textBlocks?.map((tb) => ({
                  id: tb.id,
                  text: tb.text,
                  offerIds: tb.offerIds ?? [],
                  categoryId: tb.categoryId,
                  domainId: tb.domainId,
                  queryString: tb.queryString ?? null,
                  isActive: tb.isActive,
                  position: tb.position,
                  category: tb.category,
              }))
            : [],
        testConfig: form.testConfig ?? null,
        supportedCountries: form.supportedCountries,
        tCPAMessages: form.tCPAMessages ?? null,
        categoryName: form.categoryName ?? null,
        stepToFetchBuyers: form.stepToFetchBuyers,
        defaultBuyers: form.defaultBuyers ?? [],
    } as DomainForm;
};

export const getTitleFromGenericRoute = (
    keyword: string,
    DKIs: DKI[] | null,
    genericRoutes: (Route | DomainRoute)[],
    genericRoute: string | string[] | undefined,
    ts: string | string[] | undefined | null,
    referer: string | string[] | undefined,
    replaceKeyword: string | string[] | undefined,
    trafficSourceNetwork: string | undefined | null,
    region: string | undefined,
    city: string | undefined,
    category: string | undefined,
    alwaysApplyDkis = false,
): { formattedTitle: string | null; notFound: boolean; goToHome?: boolean } => {
    const keywordDKI = DKIs?.find((DKI) => {
        if (["disallowed", "ignored"].includes(DKI.type)) {
            return (
                DKI.name.toLowerCase().replace(/ /g, "-") ===
                keyword?.toLowerCase()
            );
        } else if (["disallowed_any", "go_to_home"].includes(DKI.type)) {
            return keyword
                ?.toLowerCase()
                .includes(DKI.name.toLowerCase().replace(/ /g, "-"));
        } else {
            return undefined;
        }
    });

    if (keywordDKI) {
        switch (keywordDKI.type) {
            case "ignored":
                return { formattedTitle: null, notFound: false };
            case "disallowed":
            case "disallowed_any":
                return { formattedTitle: null, notFound: true };
            case "go_to_home":
                return {
                    formattedTitle: null,
                    notFound: false,
                    goToHome: true,
                };
        }
    }

    if (!alwaysApplyDkis && !ts && !replaceKeyword) {
        return { formattedTitle: null, notFound: false };
    }

    if (
        !genericRoutes.find(
            (item) =>
                item.route === genericRoute &&
                "category" in item &&
                category === item.category.name,
        )?.ignoreKeyword &&
        DKIs
    ) {
        let temp = replacePlaceholders(keyword, {
            region,
            category,
            city,
        });
        const matchedDKIs: string[] = [];

        let index: number = 0;

        DKIs.filter((i) => i.type === "allowed").forEach((DKI) => {
            // Create a regex that handles multi-word DKIs
            const escapedDKI = DKI.name
                .toLowerCase()
                .replace(/\s+/g, "[-\\s]+");
            const regex = new RegExp(
                `(-\\b${escapedDKI}\\b-|\\b${escapedDKI}-|-${escapedDKI}\\b)`,
                "gi",
            );

            // in case keyword is exactly one DKI
            const keywordMatchesDKI = new RegExp(
                `^${DKI.name.toLowerCase().replace(/\s+/g, "[\\s-]+")}$`,
                "i",
            ).test(temp);

            if (keywordMatchesDKI) {
                matchedDKIs.push(DKI.name);
                temp = "#0";
                return;
            }

            temp = temp.replace(regex, (e) => {
                matchedDKIs.push(DKI.name);
                if (e.endsWith("-") && e.startsWith("-")) {
                    return `-#${index++}-`;
                } else if (e.endsWith("-")) {
                    return `#${index++}-`;
                } else if (e.startsWith("-")) {
                    return `-#${index++}`;
                } else {
                    return `#${index++}`;
                }
            });
        });
        const finalWords: string[] = [];
        const words = temp.split("-");

        words.forEach((word, idx) => {
            if (word.startsWith("#")) {
                const index = parseInt(word.replace("#", ""), 10);
                finalWords.push(matchedDKIs[index]);
            } else if (
                prepositions.includes(word) &&
                idx !== 0 &&
                idx !== words.length - 1 &&
                !(
                    finalWords[idx - 1].endsWith(":") ||
                    finalWords[idx - 1].endsWith(".")
                )
            ) {
                finalWords.push(word.toLowerCase());
            } else {
                finalWords.push(word.charAt(0).toUpperCase() + word.slice(1));
            }
        });
        const formattedTitle = finalWords.join(" ");
        return { formattedTitle, notFound: false };
    }
    return { formattedTitle: null, notFound: false };
};

export const getTranslation = async ({
    domain,
    locale,
}: {
    domain: string;
    locale: string;
}): Promise<TranslationContent | null> => {
    const tenant = domain?.toLowerCase().slice(0, domain.indexOf("."));

    try {
        // Use dynamic import() instead of require() to enable code splitting
        // This ensures translation files are only loaded for their specific domain
        const translationModule = (await import(
            `../i18n/${tenant}/${locale}.json`
        )) as {
            default: JSONValue;
        };
        const content =
            translationModule.default ||
            (translationModule as unknown as JSONValue);
        return { content };
    } catch {
        return null;
    }
};

export const getFilteredOffersByNumber = (offers: ListingItem[]) => {
    const filteredOffersByNumber = offers?.filter(
        (offer) =>
            offer?.proxiedNumber?.phoneNumber ||
            offer?.hiddenAttributes?.phoneNumber ||
            offer?.phoneValue ||
            offer?.attributes.phoneNumber,
    );
    return filteredOffersByNumber;
};

export const filterOffersByAttribute = (offers: ListingItem[]) => {
    const filteredOffers = offers?.filter(
        (offer) =>
            offer?.placements?.skipMainOffersList !== true &&
            offer?.placements?.hideNumberOnHeader !== true,
    );
    return filteredOffers;
};

export const offerListToShow = (getOfferListToShowObject: OfferListToShow) => {
    const { place, offers, req, res, categorySlug, isMobile, featuredOfferId } =
        getOfferListToShowObject;
    const k = req?.query?.k as string;
    const _tf_oid = getOfferId(req);

    if (!_tf_oid && req?.query?.oid) {
        setOfferId(req.query?.oid as string, req, res);
    }
    const finalOid = _tf_oid
        ? _tf_oid
        : req?.query?.oid
        ? req?.query?.oid
        : undefined;

    // A featured offer configured in the page settings from route config
    const effectiveOid = finalOid || featuredOfferId || undefined;

    if (place === "thankYou") {
        const offersToShowOnThankyou = getOffersByPlacement(
            offers,
            "thankYou",
        ) as ListingItem[];
        const filteredOffersByNumber = getFilteredOffersByNumber(
            offersToShowOnThankyou,
        );
        const filteredOffersWithOutNumber = offersToShowOnThankyou?.filter(
            (offer) =>
                !(
                    offer?.proxiedNumber?.phoneNumber ||
                    offer?.hiddenAttributes?.phoneNumber ||
                    offer?.phoneValue ||
                    offer?.attributes.phoneNumber
                ),
        );

        let selectedOfferNumber;
        let orderedListOffers;
        if (filteredOffersByNumber?.length) {
            selectedOfferNumber = filteredOffersByNumber?.find(
                (offer) => offer?.id === parseInt(effectiveOid as string, 10),
            );
            if (!selectedOfferNumber) {
                selectedOfferNumber = filteredOffersByNumber[0];
            }

            orderedListOffers = [
                selectedOfferNumber,
                ...orderListByKeyword(k, filteredOffersWithOutNumber),
            ];
        } else {
            orderedListOffers = orderListByKeyword(k, offersToShowOnThankyou);
        }

        return orderedListOffers;
    }

    if (place === "exitModal") {
        const orderedListOffers = orderListByKeyword(
            k,
            getOffersByPlacement(offers, "exitModal") as ListingItem[],
        );

        const filteredOffersByNumber =
            getFilteredOffersByNumber(orderedListOffers);

        if (categorySlug === "auto-accident") {
            let selectedOfferNumber;

            if (filteredOffersByNumber?.length) {
                selectedOfferNumber = filteredOffersByNumber?.find(
                    (offer) =>
                        offer?.id === parseInt(effectiveOid as string, 10),
                );
                if (!selectedOfferNumber) {
                    selectedOfferNumber = filteredOffersByNumber[0];
                }
                return [selectedOfferNumber];
            } else {
                const finalList = isMobile
                    ? orderedListOffers.slice(0, 1)
                    : orderedListOffers;
                return finalList;
            }
        } else {
            let sortedOrderedListOffers = orderedListOffers;

            if (effectiveOid) {
                const foundEffectiveOidByNumber = filteredOffersByNumber?.find(
                    (offer) =>
                        offer?.id === parseInt(effectiveOid as string, 10),
                );

                if (foundEffectiveOidByNumber) {
                    const effectiveOfferIndex = orderedListOffers.findIndex(
                        (offer) =>
                            offer?.id === parseInt(effectiveOid as string, 10),
                    );

                    if (effectiveOfferIndex > 0) {
                        sortedOrderedListOffers = [...orderedListOffers];
                        const [effectiveOffer] = sortedOrderedListOffers.splice(
                            effectiveOfferIndex,
                            1,
                        );
                        sortedOrderedListOffers.unshift(effectiveOffer);
                    }
                }
            }

            const finalList = isMobile
                ? sortedOrderedListOffers.slice(0, 1)
                : sortedOrderedListOffers;
            return finalList;
        }
    }
    if (place === "header") {
        const filteredOffersBySlug = getOffersByPlacement(
            offers,
            "header",
        ) as ListingItem[];

        const filteredOffersByNumber =
            getFilteredOffersByNumber(filteredOffersBySlug);

        let offer;
        if (!filteredOffersByNumber?.length) {
            offer = null;
        } else {
            if (effectiveOid) {
                offer = filteredOffersByNumber?.find(
                    (offer) =>
                        offer?.id === parseInt(effectiveOid as string, 10),
                );

                if (!offer) {
                    offer = filteredOffersByNumber[0];
                }
            } else {
                offer = filteredOffersByNumber[0];
            }
        }
        return offer ? [offer] : [];
    }
    if (place === "stickyOffer") {
        const filteredOffersBySlug = getOffersByPlacement(
            offers,
            "showStickyOffer",
        ) as ListingItem[];
        let offer;
        if (effectiveOid) {
            offer = filteredOffersBySlug?.find(
                (offer) => offer?.id === parseInt(effectiveOid as string, 10),
            );
            if (offer) {
                return [offer];
            }
        }
        return filteredOffersBySlug.slice(0, 1);
    }

    if (place === "form") {
        const filteredOffers = filterOffersByAttribute(offers);
        const filteredOffersByNumber =
            getFilteredOffersByNumber(filteredOffers);
        const offerToShow = filteredOffersByNumber?.find(
            (el) => el.id === parseInt(effectiveOid as string, 10),
        );
        if (!offerToShow && filteredOffersByNumber?.length > 0) {
            return [filteredOffersByNumber[0]];
        }
        return offerToShow ? [offerToShow] : [];
    }
};

export const initialOffersCalls = (
    domain: DomainInfo,
    req: ServerRequest | undefined,
    res: ServerResponse | undefined,
    isMobile: boolean,
    categorySlug = "",
    featuredOfferId?: number,
) => {
    const formOffer = offerListToShow({
        place: "form",
        offers: domain?.defaultOffers,
        req,
        res,
        featuredOfferId,
    });

    const exitModalOffer = offerListToShow({
        place: "exitModal",
        offers: domain?.defaultOffers,
        req,
        res,
        categorySlug: categorySlug,
        isMobile,
        featuredOfferId,
    });

    const thankYouOffers = offerListToShow({
        place: "thankYou",
        offers: domain?.defaultOffers,
        res,
        req,
        featuredOfferId,
    });

    const headerOffer = offerListToShow({
        place: "header",
        offers: domain?.defaultOffers,
        req,
        res,
        categorySlug,
        featuredOfferId,
    });
    const stickyOffer = offerListToShow({
        place: "stickyOffer",
        offers: domain?.defaultOffers,
        req,
        res,
        categorySlug,
        featuredOfferId,
    });

    return [
        formOffer,
        exitModalOffer,
        thankYouOffers,
        headerOffer,
        stickyOffer,
    ];
};

export const hasSpecialCharacters = (slug: string | null | undefined) => {
    if (slug === null || slug === undefined) {
        return false;
    }
    // Check for special characters
    const regex = /[^\w\s-]/;
    return regex.test(slug);
};

export const filterOfferByQueryParams = ({
    placement,
    query,
    parameterFunction = "hide",
}: {
    placement?: Record<string, string | boolean | string[]>;
    query?:
        | {
              [key: string]: string | string[];
          }
        | undefined;
    parameterFunction?: "show" | "hide";
}): boolean => {
    if (!placement || Object.keys(placement).length === 0) {
        return true;
    }

    const matches = Object.entries(placement).some(([key, value]) => {
        const queryValue = query?.[key];

        if (Array.isArray(value)) {
            return value.includes(queryValue as string);
        }
        if (typeof value === "boolean") {
            return queryValue === value?.toString();
        }
        return queryValue === value;
    });

    return parameterFunction === "show" ? matches : !matches;
};

export const toE164 = (phoneNumber = ""): string | undefined => {
    // Normalize input - extract only digits
    const digits = String(phoneNumber || "").replace(/\D+/g, "");

    // Case 1: 11 digits starting with '1' (full US number)
    if (digits.length === 11 && digits.startsWith("1")) {
        return `+${digits}`;
    }

    // Case 2: 10 digits (US number without country code) - auto-prepend '1'
    if (digits.length === 10) {
        return `+1${digits}`;
    }

    // Invalid format
    return undefined;
};

/**
 * Normalizes an email per Google's enhanced conversions rules so the hash matches
 * what the tracking and campaign-builder services produce.
 * https://developers.google.com/google-ads/api/docs/conversions/upload-online#prepare-data
 */
export const normalizeEmail = (email = ""): string | undefined => {
    const normalized = String(email || "")
        .toLowerCase()
        .replace(/\s+/g, "");

    if (!normalized) return undefined;

    const parts = normalized.split("@");

    // Periods and '+' suffixes are stripped from the username of gmail/googlemail addresses only.
    if (parts.length === 2 && /^(gmail|googlemail)\.com$/.test(parts[1])) {
        return `${parts[0].replace(/\./g, "").replace(/\+.*/, "")}@${parts[1]}`;
    }

    return normalized;
};

export const handleFormAnswers = (visitDetails: { visitId: string }) => {
    const formAnswers = JSON.parse(
        localStorage.getItem("globalFormValues") as string,
    ) as Record<string, unknown>;

    const phoneNumber_e164 = toE164(
        (formAnswers?.["phoneNumber"] as string) || "",
    );
    const normalizedEmail = normalizeEmail(
        (formAnswers?.["email"] as string) || "",
    );

    const phoneNumberHashed = phoneNumber_e164
        ? sha256(phoneNumber_e164)
        : undefined;
    const emailHashed = normalizedEmail ? sha256(normalizedEmail) : undefined;

    // @ts-ignore
    window.formAnswers = {
        ...formAnswers,
        phoneNumberHashed,
        emailHashed,
        phoneNumber_e164,
        externalId: sha256(visitDetails.visitId),
    };
};
const allowedSlugsForMakersApi = [
    "auto-warranty",
    "car-warranty",
    "autowarranty",
];
// domains with type informational that need to get form
const informationalDomainsToGetForm = [
    "severalbrands.com",
    "premiumcoverage.com",
];

export const shouldFetchDomainForm = ({
    domain,
    options,
    pageType,
    props,
}: {
    domain: DomainInfo;
    options?: Options;
    pageType: string;
    props: Partial<PageProps>;
}): boolean => {
    return (
        (domain.categories.length === 1 &&
            !options?.isStaticContent &&
            (domain.type === "lead" || options?.fetchForm)) ||
        (pageType === "blogPost" && domain.config?.withForm === true) ||
        informationalDomainsToGetForm.includes(domain?.name) ||
        (domain.type === "lead" && pageType === "thankyou") ||
        (domain.categories.length > 1 &&
            domain.type === "lead" &&
            !!props.category &&
            pageType !== "home") ||
        pageType === "consent-completion"
    );
};

export const shouldFetchMakerList = ({
    domain,
    props,
    pageType,
}: {
    domain: DomainInfo;
    props: Partial<PageProps>;
    pageType: string;
}): boolean => {
    const allowedPagesTypes = ["genericRoute", "category", "home"];
    return (
        allowedSlugsForMakersApi.includes(props.category?.slug as string) &&
        !domain.config?.disableAutoWarrantyMakersList &&
        allowedPagesTypes.includes(pageType)
    );
};

export const fillPageProps = ({
    results,
    props,
}: {
    results: {
        form?: { data?: DomainForm | null };
        makers?: { data?: AutoWarrantyMakers };
        blogPost?: { data?: BlogPostType | null };
        thankyouListing?: { data?: ListingV3 | null };
        secondListing?: { data?: ListingV3 | undefined };
        ClicksSearchList?: { data?: ClicksSearchList | undefined };
        doNotSellFormStates?: { data?: LookupOrVariationItem[] | null };
        piiCompletionData?: { data?: PiiCompletion | null };
        nearbyZipCodes?: { data?: string[] | null };
        textBlocks?: { data?: GetTextBlocksResponse | null };
    };
    props: Partial<PageProps>;
}): Partial<PageProps> => {
    const {
        form,
        makers,
        thankyouListing,
        secondListing,
        ClicksSearchList,
        blogPost,
        doNotSellFormStates,
        piiCompletionData,
        nearbyZipCodes,
        textBlocks,
    } = results;

    return {
        ...props,
        form: mappingFormInfo(form?.data ?? null),
        autoWarrantyMakers: makers?.data ?? ([] as AutoWarrantyMakers),
        thankyouListing: thankyouListing?.data ?? null,
        secondListing: secondListing?.data ?? [],
        ClicksSearchList: ClicksSearchList?.data ?? null,
        blogPost: blogPost?.data ?? null,
        doNotSellFormStates: doNotSellFormStates?.data ?? null,
        piiCompletionData: piiCompletionData?.data ?? null,
        nearbyZipCodes: nearbyZipCodes?.data ?? null,
        textBlocks: textBlocks?.data ?? null,
    } as Partial<PageProps>;
};

export const shouldOverrideThankYouListing = ({
    domain,
    form,
    currentCategory,
}: {
    domain: DomainInfo;
    form: DomainForm | null | undefined;
    currentCategory: Category | null | undefined;
}): boolean => {
    return !!(
        form?.listing?.slug &&
        (domain.categories.length === 1 || currentCategory) &&
        domain.type === "lead"
    );
};

export async function executeParallelCalls<
    T extends Record<string, Promise<unknown>>,
>(
    calls: T,
): Promise<{
    [K in keyof T]: T[K] extends Promise<infer R> ? R : never;
}> {
    const keys = Object.keys(calls) as Array<keyof T>;
    const promises = Object.values(calls);
    const results = await Promise.allSettled(promises);

    const output = {} as {
        [K in keyof T]: T[K] extends Promise<infer R> ? R : never;
    };

    results.forEach((result, index) => {
        const key = keys[index];
        if (result.status === "fulfilled") {
            output[key] = result.value as T[typeof key] extends Promise<infer R>
                ? R
                : never;
        } else {
            output[key] = null as T[typeof key] extends Promise<infer R>
                ? R
                : never;
        }
    });

    return output;
}

/**
 * Parses Base64-encoded form data from query string
 */
export const parseFormDataFromQuery = (
    queryData: string | undefined,
): Record<string, string> | null => {
    if (!queryData) return null;

    try {
        return JSON.parse(
            Buffer.from(queryData, "base64").toString("utf-8"),
        ) as Record<string, string>;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to parse form data:", error);
        return null;
    }
};

/**
 * Transforms form fields to API question format
 */
export const transformToQuestionFormat = (
    fieldsObject: Record<string, string>,
): Record<string, { name: string; value: string }> => {
    const questions: Record<string, { name: string; value: string }> = {};

    Object.keys(fieldsObject).forEach((key) => {
        questions[key] = {
            name: key,
            value: fieldsObject[key],
        };
    });

    return questions;
};
export const isPageHaveFormTemplate = (options?: Options): boolean => {
    return options?.activateOfferFormTemplate ?? false;
};

export const isClaimsPageUrl = (url: string): boolean => {
    // strip query string
    const pathOnly = url.split("?")[0];
    const claimsRegex = /(^|\/)claims(\/|$)/;

    return claimsRegex.test(pathOnly);
};

interface RedirectResult {
    redirect: {
        destination: string;
        permanent: boolean;
    };
}

export const redirectUserToMainCategoryPage = ({
    domain,
    props,
    path,
}: {
    domain: DomainInfo;
    props: Partial<PageProps>;
    path?: string;
}): RedirectResult => {
    const slug = props.category?.slug as string;
    const destination =
        domain.categories.length > 1 ? `/${slug}` : path ? path : "/";
    return {
        redirect: {
            destination,
            permanent: false,
        },
    };
};

export const getMakers = async (req: ServerRequest) => {
    const data = await getLookupsAndVariations({
        req,
        source: "lookups",
        queries: {
            orderBy: "label",
            orderByDirection: "asc",
            type: "make",
            variationsIds: undefined,
            lookupsId: undefined,
        },
    });
    return data ?? [];
};

export const gettingCategory = (
    categorySlugAlias: string,
    domainCategories: Category[],
): Category | undefined => {
    return domainCategories.find((category) => {
        return (
            category.slugAlias === categorySlugAlias ||
            category.slug === categorySlugAlias
        );
    });
};

// export const isClaimsPageWithoutPhoneHeaderOffer = ({
//     context,
//     props,
// }: {
//     context: GetServerSidePropsContext<ParsedUrlQuery>;
//     props: Partial<PageProps>;
// }): boolean => {
//     return (
//         isClaimsPageUrl(context.resolvedUrl) &&
//         props.category?.slug === "auto-accident" &&
//         !props["headerOffer"]?.length
//     );
// };

export const isCategoryExist = (
    categorySlugAlias: string,
    domain: DomainInfo,
): boolean => {
    if (
        domain.categories.find((domainCategory: Category) => {
            return domainCategory.slugAlias === categorySlugAlias;
        })
    ) {
        return true;
    } else if (
        domain.categories.find(
            (domainCategory: Category) =>
                domainCategory.slug === categorySlugAlias,
        )
    ) {
        return true;
    }
    return false;
};

export const isListingExist = (
    listingSlug: string,
    domain: DomainInfo,
): boolean => {
    if (
        !domain.listings.find(
            (domainCategory) => domainCategory.slug === listingSlug,
        )
    ) {
        return false;
    }
    return true;
};

export const handleOptions = async ({
    options,
    props,
    req,
}: {
    options: Options;
    props: Partial<PageProps>;
    req: ServerRequest;
}): Promise<Partial<PageProps>> => {
    const tempProps: Partial<PageProps> & {
        [key: string]: any;
    } = { ...props };

    for (const option of Object.keys(options)) {
        switch (option) {
            case "getZipCodeLocation": {
                if (options.getZipCodeLocation?.zipCode) {
                    const { data: location } = await getLocationByZipCode({
                        zipCode: options.getZipCodeLocation.zipCode,
                        req,
                    });

                    tempProps["zipCodeLocation"] = location;
                }

                break;
            }

            case "hasOperatingSystem": {
                const os = resolveOperatingSystem(req.headers["user-agent"]);

                tempProps["operatingSystem"] = os;

                break;
            }

            case "pageTitle": {
                if (props.formattedTitle) {
                    tempProps["pageTitle"] = props.formattedTitle?.replace(
                        /<br>/g,
                        "",
                    );
                } else {
                    tempProps["pageTitle"] = decodePageTitle(
                        options.pageTitle as string,
                        props,
                    );
                }

                break;
            }

            case "extraProps": {
                if (options.extraProps) {
                    for (const extraPropKey of Object.keys(
                        options.extraProps,
                    )) {
                        tempProps[extraPropKey] =
                            options.extraProps[extraPropKey];
                    }
                }
                break;
            }
        }
    }

    return tempProps;
};

export const decodePageTitle = (text: string, props: Partial<PageProps>) => {
    let clonedText = text;

    const dynamicStrings = text?.match(/\${([^}]*)}/g);

    dynamicStrings?.forEach((item) => {
        if (
            get(props, item.replace("${", "").replace("}", "").replace("?", ""))
        ) {
            const temp = clonedText.replace(
                item,
                get(
                    props,
                    item.replace("${", "").replace("}", "").replace("?", ""),
                ) as string,
            );

            clonedText = temp;
        } else {
            clonedText = text.replace(item, "");
        }
    });

    return clonedText;
};

export const orderingList = (
    keyword: string | undefined,
    list: ListingItem[],
): ListingItem[] => {
    if (keyword && typeof keyword === "string") {
        const listItemMatched = list?.find((item) => {
            if (item.slug.indexOf(keyword.toLocaleLowerCase()) > -1) {
                return item;
            }
        });
        if (listItemMatched) {
            listItemMatched.hiddenAttributes.scoreValue = "9.9";
            listItemMatched.hiddenAttributes.scoreMsg = "Exceptional";

            return [
                listItemMatched,
                ...list.filter((item) => item.slug !== listItemMatched.slug),
            ];
        }

        return list;
    }
    return list;
};

export const isNotAllowedToOpenThankYouPage = (
    query: ParsedUrlQuery,
): boolean => {
    return !!(
        query.slug?.includes("thank-you") &&
        !query?.data &&
        query?.previewPb !== "true"
    );
};

/**
 * Determines and assigns the correct category to props based on domain, pageType, and context
 */
export const assignCategoryToProps = ({
    props,
    domain,
    pageType,
    categorySlugAlias,
    query,
    options,
}: {
    props: Partial<PageProps>;
    domain: DomainInfo;
    pageType: string;
    categorySlugAlias?: string | string[];
    routeConfig?: RouteConfig | null;
    query?: ParsedUrlQuery;
    options?: Options;
}): void => {
    // 1. If category already set (e.g., from page-builder replacedPortions), keep it
    if (props.category) {
        return;
    }

    // 2. For single-category domains, always use the first category
    if (domain.categories.length === 1) {
        props.category = mappingCategory(domain.categories[0]);
        return;
    }

    if (options?.slug) {
        const category = gettingCategory(options?.slug, domain.categories);
        if (category) {
            props.category = mappingCategory(category);
            return;
        }
    }

    // 3. For multi-category domains, determine category based on context

    // 3.1 For page-builder without category, set possible category from slug
    if (pageType === "page-builder" && !props.category) {
        const possibleCategory = setPossibleCategory({
            domain,
            query: query ?? {},
        });
        if (possibleCategory) {
            props.category = mappingCategory(possibleCategory);
            return;
        }
    }

    // 3.2 For pages with categorySlugAlias parameter
    if (categorySlugAlias) {
        const category = gettingCategory(
            categorySlugAlias as string,
            domain.categories,
        );
        if (category) {
            props.category = mappingCategory(category);
            return;
        }
    }

    // 3.3 For thankyou page with data in query
    if (pageType === "thankyou" && query?.data) {
        try {
            const fieldsObject = JSON.parse(
                Buffer.from(query.data as string, "base64").toString("utf-8"),
            ) as { [key: string]: string };

            // Try to extract category from form data
            const categorySlug = fieldsObject.category;
            if (categorySlug) {
                const category = gettingCategory(
                    categorySlug,
                    domain.categories,
                );
                if (category) {
                    props.category = mappingCategory(category);
                    return;
                }
            }
        } catch (e) {
            // If parsing fails, continue to fallback
        }
    }

    // 3.4 For blogPost with blog data
    if (pageType === "blogPost" && props.blogPost?.model?.category?.slug) {
        const category = gettingCategory(
            props.blogPost.model.category.slug,
            domain.categories,
        );
        if (category) {
            props.category = mappingCategory(category);
            return;
        }
    }

    // 3.5 For form-completion/consent-completion with piiCompletionData
    if (pageType === "form-completion" && props.piiCompletionData?.category) {
        props.category = mappingCategory(props.piiCompletionData.category);
        return;
    }

    if (pageType === "consent-completion") {
        props.category = mappingCategory(domain.categories[0]);
        return;
    }

    // 4. Fallback: use first category
    props.category = mappingCategory(domain.categories[0] ?? null);
    props.defaultFallbackCategory = true;
};

export const setPossibleCategory = ({
    query,
    domain,
}: {
    query: ParsedUrlQuery;
    domain: DomainInfo;
}): Category | undefined => {
    let possibleCategory: Category | undefined;

    if (Array.isArray(query.slug)) {
        const foundCategory = domain.categories.find(
            (category) =>
                (query.slug as any[]).includes(category.slug) ||
                (query.slug as any[]).includes(category.slugAlias),
        );
        if (foundCategory) {
            possibleCategory = foundCategory;
        }
    }

    return possibleCategory;
};

export const setFavIcon = ({
    domain,
    routeConfig,
    props,
}: {
    domain: DomainInfo;
    routeConfig: RouteConfig | null;
    props: Partial<PageProps>;
}): void => {
    const variantConfigFavIcon =
        routeConfig?.templateConfig?.variantConfig?.favicon;
    const pageFavIcon = routeConfig?.config?.settings?.favicon;
    const domainFavIcon = domain?.favicon?.fullPath;

    props["externalFavicon"] =
        pageFavIcon ?? variantConfigFavIcon ?? domainFavIcon ?? null;
};

export const prepareComponentsApiCalls = ({
    routeConfig,
    props,
    query,
}: {
    routeConfig: RouteConfig | null;
    props: Partial<PageProps>;
    query?: ParsedUrlQuery;
}): IComponentApiCall[] => {
    // Use a Map instead of Set -> key is "method:value"
    const apiCallsToBeMade = new Map<string, IComponentApiCall>();

    routeConfig?.config?.sections?.forEach((section) => {
        section.components?.forEach((component) => {
            switch (component.id) {
                case "Form": {
                    let categorySlug: string | undefined;

                    if (component.props.usePostCategory) {
                        categorySlug =
                            props.pageBuilderBlogPost?.category?.slug;
                    } else if (
                        !component.props.formCategory ||
                        component.props.formCategory?.auto
                    ) {
                        categorySlug = props.category?.slug;
                    } else if (
                        component.props.formCategory?.categories?.length === 1
                    ) {
                        categorySlug =
                            component.props.formCategory?.categories?.[0]?.slug;
                    }

                    if (categorySlug) {
                        apiCallsToBeMade.set(`getForm:${categorySlug}`, {
                            key: "getForm",
                            value: categorySlug,
                        });
                        apiCallsToBeMade.set(`getTextBlocks:${categorySlug}`, {
                            key: "getTextBlocks",
                            value: {
                                categorySlug,
                                positions: [...FORM_TEXT_BLOCK_POSITIONS],
                            },
                        });
                    }
                    break;
                }
                case "TermsAndConditions":
                case "PrivacyPolicy": {
                    const categorySlug = props.category?.slug;
                    apiCallsToBeMade.set(
                        `getTextBlocks:${categorySlug as string}`,
                        {
                            key: "getTextBlocks",
                            value: {
                                categorySlug: categorySlug as string,
                                positions: [...LEGAL_TEXT_BLOCK_POSITIONS],
                            },
                        },
                    );
                    break;
                }

                case "ThankYou": {
                    const slug = props.category?.slug;
                    if (slug) {
                        apiCallsToBeMade.set(`getForm:${slug}`, {
                            key: "getForm",
                            value: slug,
                        });
                    }
                    break;
                }

                case "LookupsList": {
                    if (component.props.lookup) {
                        apiCallsToBeMade.set(
                            `getLookup:${component.props.lookup}`,
                            {
                                key: "getLookup",
                                value: component.props.lookup,
                            },
                        );
                    }
                    break;
                }

                case "AffiliateOfferList": {
                    if (
                        component.props.source &&
                        !["auto", "external"].includes(component.props.source)
                    ) {
                        apiCallsToBeMade.set(
                            `getList:${component.props.source}`,
                            {
                                key: "getList",
                                value: component.props.source,
                            },
                        );
                    }

                    if (component.props.source === "external") {
                        apiCallsToBeMade.set(`getExternalList:true`, {
                            key: "getExternalList",
                            value: true,
                        });
                    }
                    break;
                }
                case "StatesPins": {
                    const slug = props?.category?.slug;
                    if (slug) {
                        apiCallsToBeMade.set(`getNearbyZipCodes:${slug}`, {
                            key: "getNearbyZipCodes",
                            value: slug,
                        });
                    }
                    break;
                }
                case "ContactUsForm": {
                    apiCallsToBeMade.set(`getLookup:crm-company-states`, {
                        key: "getLookup",
                        value: "crm-company-states",
                    });
                    break;
                }
                case "PostsGrid": {
                    // Add domain posts API call if PostsGrid component exists
                    const postsGridApiCall = buildPostsGridApiCall({
                        component,
                        query,
                    });

                    apiCallsToBeMade.set(postsGridApiCall.key, {
                        key: "getDomainPosts",
                        value: postsGridApiCall.value,
                    });

                    break;
                }
            }
        });
    });

    // Check if FooterTemplate3 is being used and add menus API call
    const isFooterTemplate3 =
        routeConfig?.templateConfig?.headerAndFooter?.footer?.footerTemplate ===
        "template-3";
    if (isFooterTemplate3) {
        apiCallsToBeMade.set(`getMenus:true`, {
            key: "getMenus",
            value: true,
        });
    }

    // Return deduplicated calls
    return Array.from(apiCallsToBeMade.values());
};

export const prepareApiPromises = ({
    apiCallsToBeMade,
    pageDataCalls,
    req,
    res,
}: {
    pageDataCalls: PageDataCalls;
    apiCallsToBeMade: IComponentApiCall[];
    req: ServerRequest;
    res: ServerResponse;
}) => {
    const promises: TypedApiCall[] = [];
    // Loop over apiCallsToBeMade and push to promises array
    apiCallsToBeMade.forEach((call: IComponentApiCall) => {
        const { key, value } = call;
        // Skip externalListing as it's handled separately
        if (key === "getExternalList") {
            return;
        }

        let apiCallType: ApiCallType;
        let promise: ReturnType<
            | typeof getDomainForm
            | typeof getLookupsAndVariations
            | typeof getListingBySlugV3
            | typeof getNearbyZipCodes
            | typeof getTextBlocks
            | typeof getMenus
            | typeof getDomainPosts
        >;
        switch (key) {
            case "getForm": {
                apiCallType = ApiCallType.FORM;
                promise = getDomainForm({
                    req,
                    res,
                    categorySlug: value as string,
                });

                pageDataCalls.getDomainForm = promise;

                break;
            }
            case "getTextBlocks": {
                apiCallType = ApiCallType.TEXTBLOCKS;
                promise = getTextBlocks(
                    {
                        categorySlug: value.categorySlug as string,
                        positions: value.positions as string[],
                    },
                    req,
                );

                pageDataCalls.getTextBlocks = promise;

                break;
            }

            case "getLookup": {
                apiCallType = ApiCallType.LOOKUP;
                promise = getLookupsAndVariations({
                    req,
                    source: "lookups",
                    queries: {
                        orderBy: "label",
                        orderByDirection: "asc",
                        type: value as string,
                        variationsIds: undefined,
                        lookupsId: undefined,
                    },
                });

                if (!pageDataCalls.getLookupsAndVariations) {
                    pageDataCalls.getLookupsAndVariations = [];
                }
                pageDataCalls.getLookupsAndVariations.push(promise);
                break;
            }

            case "getList": {
                apiCallType = ApiCallType.LISTING;
                promise = getListingBySlugV3({
                    req,
                    res,
                    slug: value as string,
                });

                if (!pageDataCalls.getListingBySlugV3) {
                    pageDataCalls.getListingBySlugV3 = [];
                }
                pageDataCalls.getListingBySlugV3.push(promise);
                break;
            }
            case "getNearbyZipCodes": {
                apiCallType = ApiCallType.NEARBY_ZIP_CODES;

                promise = getNearbyZipCodes({
                    req,
                    category: value as string,
                });

                pageDataCalls.getNearbyZipCodes = promise;

                break;
            }
            case "getMenus": {
                apiCallType = ApiCallType.MENUS;

                promise = getMenus(req);

                pageDataCalls.getMenus = promise;

                break;
            }
            case "getDomainPosts": {
                apiCallType = ApiCallType.DOMAIN_POSTS;

                promise = getDomainPosts(
                    value as Partial<GetDomainPostsParams>,
                    req,
                );

                if (!pageDataCalls.getDomainPosts) {
                    pageDataCalls.getDomainPosts = [];
                }
                pageDataCalls.getDomainPosts.push(promise);

                break;
            }
            default:
                return; // Skip unknown types
        }

        const typedCall = {
            promise,
            type: apiCallType,
            identifier: value,
            timestamp: Date.now(),
        };

        promises.push(typedCall);
    });
    return promises;
};

export const domainOffersDistributionHandler = ({
    domain,
    props,
    session,
    req,
    res,
    featuredOfferId,
}: {
    req: ServerRequest | undefined;
    res: ServerResponse | undefined;
    domain: DomainInfo;
    props: Partial<PageProps>;
    session: Session;
    featuredOfferId?: number;
}): {
    headerOffer: ListingItem[] | undefined;
    formOffer: ListingItem[] | undefined;
    exitModalOffer: ListingItem[] | undefined;
    thankYouOffers: ListingItem[] | undefined;
    stickyOffer: ListingItem[] | undefined;
} => {
    const [
        formOffer,
        exitModalOffer,
        thankYouOffers,
        headerOffer,
        stickyOffer,
    ] = initialOffersCalls(
        domain,
        req,
        res,
        session.visitDetails.isMobile,
        props.category?.slug,
        featuredOfferId,
    );

    props["formOffer"] = formOffer ?? [];
    props["exitModalOffer"] = exitModalOffer ?? [];
    props["thankYouOffers"] = thankYouOffers ?? [];
    props["headerOffer"] = headerOffer ?? [];
    props["stickyOffer"] = stickyOffer ?? [];
    return {
        headerOffer,
        formOffer,
        exitModalOffer,
        thankYouOffers,
        stickyOffer,
    };
};

export const getExternalList = async ({
    domain,
    query,
    props,
    req,
    res,
    skipCrossSellingDomainFormId,
    offersSizeFromExternalApi,
}: {
    domain: DomainInfo;
    query: ParsedUrlQuery;
    props: Partial<PageProps>;
    req: ServerRequest | undefined;
    res: ServerResponse | undefined;
    skipCrossSellingDomainFormId?: boolean;
    offersSizeFromExternalApi?: number;
}): Promise<ClickOffer[] | null> => {
    // crossSellingDomainFormId exists on domain category not on category
    const crossSellingDomainFormId = domain.categories.find(
        (c) => c.slug === props["category"]?.slug,
    )?.crossSellingDomainFormId;

    if (!crossSellingDomainFormId && !skipCrossSellingDomainFormId) {
        return null;
    }

    type Data = { [x: string]: string };

    let fieldsObject: Data = {};

    try {
        fieldsObject = JSON.parse(
            Buffer.from(query.data as string, "base64").toString("utf-8"),
        ) as Data;
    } catch {
        return null;
    }

    const finalQuestionsObject: Record<
        string,
        { name: string; value: string }
    > = {};

    Object.keys(fieldsObject).forEach((item) => {
        finalQuestionsObject[item] = {
            name: item,
            value: fieldsObject[item],
        };
    });

    const { data: clicksSearchList } = await getClicksSearchList({
        questions: {
            ...finalQuestionsObject,
        },
        categorySlug: props["category"]?.slug as string,
        crossSellingDomainFormId,
        req,
        res,
        offersSizeFromExternalApi,
    });

    // Use the optimized helper function to process offers
    return processAndAssignClicksOffers({ clicksSearchList });
};

export const getFullRoutePath = (slug: string | string[] | undefined) => {
    if (Array.isArray(slug)) {
        return slug.join("/");
    }
    return slug ?? "/";
};

export const redirectTo = (
    destination: string,
): {
    redirect: { destination: string; permanent: false };
} => {
    return {
        redirect: {
            destination,
            permanent: false,
        },
    };
};

export const getUrlAliasShortenerRedirection = async ({
    req,
    alias,
}: {
    req: ServerRequest;
    alias: string | string[] | undefined;
}): Promise<
    | { notFound: true }
    | { redirect: { destination: string; permanent: boolean } }
> => {
    if (!alias) {
        return {
            notFound: true,
        };
    }
    const { data: url, error } = await getAliasUrl({
        req,
        alias: alias as string,
    });

    if (!url || error) {
        return {
            notFound: true,
        };
    } else {
        return {
            redirect: {
                destination: url,
                permanent: true,
            },
        };
    }
};

/**
 * Processes clicks search list results and appends tracking ID to offer slugs
 * Returns a new array with updated slugs without mutating the original data
 * @param clicksSearchList - The search list containing offers and list request ID
 * @returns Array of click offers with tracking IDs appended, or null if empty
 */
export function processAndAssignClicksOffers({
    clicksSearchList,
}: {
    clicksSearchList: ClicksSearchList | null | undefined;
}): ClickOffer[] | null {
    // Early return if no results
    if (!clicksSearchList?.result?.length) {
        return null;
    }

    const listRequestId = clicksSearchList.listRequestId;

    // Map to new array instead of mutating original
    return clicksSearchList.result.map((offer) => ({
        ...offer,
        slug: `${offer.slug}?id=${listRequestId}`,
    }));
}

// Define text block position constants
export const LEGAL_TEXT_BLOCK_POSITIONS = [
    "custom-domain-privacy-policy",
    "custom-category-privacy-policy",
    "custom-domain-terms-and-conditions",
    "custom-category-terms-and-conditions",
    "custom-domain-ccpa-privacy-notice",
    "custom-category-ccpa-privacy-notice",
] as const;

export const FORM_TEXT_BLOCK_POSITIONS = [
    "form-inside-exit-modal-submit-button-text-second-step",
    "form-inside-exit-modal-submit-button-text",
    "form-inside-exit-modal-first-step-title",
    "form-inside-exit-modal-second-step-title",
    "form_title",
    "form_description",
] as const;

export const extractTextBlocks = (
    legalTextBlocksResponse: GetTextBlocksResponse | null,
): [TextBlock[], TextBlock[]] => {
    const allTextBlocks = legalTextBlocksResponse?.data ?? [];

    const legalPositionsSet = new Set(LEGAL_TEXT_BLOCK_POSITIONS);
    const formPositionsSet = new Set(FORM_TEXT_BLOCK_POSITIONS);

    const legalTextBlocks = allTextBlocks.filter((block) =>
        legalPositionsSet.has(block.position),
    );

    const formTextBlocks = allTextBlocks.filter((block) =>
        formPositionsSet.has(block.position),
    );

    return [legalTextBlocks, formTextBlocks];
};

export const buildQueryParams = (params: Record<string, any>): string => {
    const queryParts: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            const encodedValue = Array.isArray(value)
                ? value.join(",")
                : String(value);
            queryParts.push(`${key}=${encodedValue}`);
        }
    });

    return queryParts.join("&");
};
